import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LoadingState } from "@/components/loading-state";
import { ErrorState } from "@/components/error-state";
import { EmptyState } from "@/components/empty-state";
import { DetailModal } from "@/components/detail-modal";
import { useAuth } from "@/app/auth-context";
import { dailyReconApi, type DailyReconException, type DailyReconListItem } from "@/lib/daily-recon";
import { PageHeader } from "@/components/page-header";
import { RefreshCw } from "lucide-react";

function fmt(n: number) {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function DailyReconPage() {
  const qc = useQueryClient();
  const { permissions } = useAuth();
  const canRun = permissions.has("reconciliation:run");
  const canViewToday = permissions.has("reconciliation:read");
  const [activeTab, setActiveTab] = useState(canViewToday ? "today" : "history");
  const [detailId, setDetailId] = useState<number | null>(null);
  const today = useQuery({ queryKey: ["recon-today"], queryFn: dailyReconApi.today, enabled: canViewToday });
  const history = useQuery({ queryKey: ["recon-history"], queryFn: () => dailyReconApi.history(50) });
  const detail = useQuery({
    queryKey: ["recon-detail", detailId],
    queryFn: () => (detailId !== null ? dailyReconApi.detail(detailId) : Promise.resolve(null)),
    enabled: detailId !== null,
  });
  const run = useMutation({
    mutationFn: dailyReconApi.run,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["recon-today"] });
      await qc.invalidateQueries({ queryKey: ["recon-history"] });
    },
  });

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader title="Daily Recon" />
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <TabsList>
            {canViewToday ? <TabsTrigger value="today">Today</TabsTrigger> : null}
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>
          
          {activeTab === "today" && today.data ? (
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <Badge variant={today.data.status === "Balanced" ? "success" : "warning"}>
                {today.data.status}{today.data.isPreview ? " (preview)" : ""}
              </Badge>
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                Accounting day {today.data.accountingDate}
              </span>
              {today.data.runId !== null && today.data.pdfFileName ? (
                <Button variant="outline" size="sm" onClick={() => void dailyReconApi.downloadPdf(today.data!.runId!, today.data!.pdfFileName!)}>
                  Download PDF
                </Button>
              ) : null}
              {canRun ? (
                <Button variant="outline" size="icon" disabled={run.isPending} onClick={() => run.mutate()} title="Run / refresh recon">
                  <RefreshCw className={`h-4 w-4 ${run.isPending ? "animate-spin" : ""}`} />
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>

        {canViewToday ? (
          <TabsContent value="today">
            {today.isLoading ? (
              <LoadingState />
            ) : today.isError ? (
              <ErrorState onRetry={() => void today.refetch()} />
            ) : today.data ? (
              <div className="flex flex-col gap-4">
                {run.isError ? (
                  <p className="text-sm text-destructive">Recon run failed. Please try again.</p>
                ) : null}

                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  <Card
                    label="Invoices eligible"
                    value={`${today.data.invoicesEligibleCount} / ${fmt(today.data.invoicesEligibleIncGst)}`}
                  />
                  <Card
                    label="Invoices pending export"
                    value={`${today.data.invoicesPendingCount} / ${fmt(today.data.invoicesPendingIncGst)}`}
                  />
                  <Card
                    label="Payments pending export"
                    value={`${today.data.paymentsPendingCount} / ${fmt(today.data.paymentsPendingTotal)}`}
                  />
                  <Card
                    label="MYOB status"
                    value={today.data.fullyExported ? "Fully exported" : "Backlog"}
                  />
                  <Card label="Accounts checked" value={String(today.data.accountsCheckedCount)} />
                  <Card
                    label="Accounts out of balance"
                    value={String(today.data.accountsOutOfBalanceCount)}
                  />
                  <Card
                    label="EOD invoices total"
                    value={
                      today.data.eodTotalsPresent
                        ? fmt(today.data.eodInvoicesTodayTotal)
                        : "no EOD totals"
                    }
                  />
                  <Card label="Exceptions" value={String(today.data.exceptionCount)} />
                </div>

                {today.data.exceptions.length === 0 ? (
                  <EmptyState headline="No exceptions — everything reconciles." />
                ) : (
                  <ExceptionTable rows={today.data.exceptions} />
                )}
              </div>
            ) : null}
          </TabsContent>
        ) : null}

        <TabsContent value="history">
          {history.isLoading ? (
            <LoadingState />
          ) : history.isError ? (
            <ErrorState onRetry={() => void history.refetch()} />
          ) : (history.data ?? []).length === 0 ? (
            <EmptyState headline="No recon runs yet." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Accounting date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Exceptions</TableHead>
                  <TableHead>Run at</TableHead>
                  <TableHead>By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(history.data ?? []).map((r: DailyReconListItem) => (
                  <TableRow
                    key={r.runId}
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() => setDetailId(r.runId)}
                  >
                    <TableCell>{r.accountingDate}</TableCell>
                    <TableCell><Badge variant={r.status === "Balanced" ? "success" : "warning"}>{r.status}</Badge></TableCell>
                    <TableCell>{r.exceptionCount}</TableCell>
                    <TableCell>{new Date(r.runAtUtc).toLocaleString()}</TableCell>
                    <TableCell>{r.runByName}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TabsContent>
      </Tabs>

      <DetailModal
        open={detailId !== null}
        onOpenChange={(open) => { if (!open) setDetailId(null); }}
        title={detail.data ? `Recon — ${detail.data.accountingDate}` : "Recon detail"}
      >
        {detail.isLoading ? (
          <LoadingState />
        ) : detail.data ? (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Badge variant={detail.data.status === "Balanced" ? "success" : "warning"}>
                {detail.data.status}
              </Badge>
              {detail.data.runAtUtc ? (
                <span className="text-sm text-muted-foreground">
                  {new Date(detail.data.runAtUtc).toLocaleString()}
                  {detail.data.runByName ? ` · ${detail.data.runByName}` : ""}
                </span>
              ) : null}
              {detail.data.runId !== null && detail.data.pdfFileName ? (
                <Button variant="outline" onClick={() => void dailyReconApi.downloadPdf(detail.data!.runId!, detail.data!.pdfFileName!)}>
                  Download PDF
                </Button>
              ) : null}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Card
                label="Invoices eligible"
                value={`${detail.data.invoicesEligibleCount} / ${fmt(detail.data.invoicesEligibleIncGst)}`}
              />
              <Card
                label="Invoices pending export"
                value={`${detail.data.invoicesPendingCount} / ${fmt(detail.data.invoicesPendingIncGst)}`}
              />
              <Card
                label="Payments pending export"
                value={`${detail.data.paymentsPendingCount} / ${fmt(detail.data.paymentsPendingTotal)}`}
              />
              <Card
                label="Accounts out of balance"
                value={String(detail.data.accountsOutOfBalanceCount)}
              />
            </div>
            {detail.data.exceptions.length === 0 ? (
              <EmptyState headline="No exceptions for this run." />
            ) : (
              <ExceptionTable rows={detail.data.exceptions} />
            )}
          </div>
        ) : null}
      </DetailModal>
    </div>
  );
}

function Card({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border p-3 bg-white shadow-sm">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-medium">{value}</div>
    </div>
  );
}

function ExceptionTable({ rows }: { rows: DailyReconException[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Category</TableHead>
          <TableHead>Reference</TableHead>
          <TableHead>Expected</TableHead>
          <TableHead>Actual</TableHead>
          <TableHead>Difference</TableHead>
          <TableHead>Detail</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((e, i) => (
          <TableRow key={i}>
            <TableCell>{e.category}</TableCell>
            <TableCell>{e.reference}</TableCell>
            <TableCell>{fmt(e.expectedAmount)}</TableCell>
            <TableCell>{fmt(e.actualAmount)}</TableCell>
            <TableCell>{fmt(e.difference)}</TableCell>
            <TableCell>{e.detail}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
